import Grid from 'components/core/Grid';
import { GridSize } from '@mui/material';
import { Skeleton } from '@mui/lab';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { Box, ButtonBase } from '@mui/material';
import { theme } from '../../../GlobalStyles';

const LabelText = styled(Typography)`
  ${props => props.theme.typography.body2};
  padding-right: ${props => props.theme.spacing(1)};
`;

const ValueText = styled(Typography)`
  ${props => props.theme.typography.body2};

  & .uppercase {
    text-transform: uppercase;
  }
`;

interface Props {
  label: string;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  fontSize?: number | string;
  fontWeight?: number | string;
  value: string | number | React.ReactNode | undefined;
  variant?: 'basic' | 'accounting' | 'dollar' | 'button';
  dangerouslySetHTML?: boolean;
  hasBorder?: boolean;
  setHTML?: () => any;
  isMonetaryValue?: boolean;
  isIndented?: boolean;
  isNegativeValue?: boolean;
  onClick?: () => void;
  whiteSpace?: 'pre-line';
}

// TODO - This component needs to be standardized to use the Skeleton
// component when appropriate.
// TODO - Remove all of the number variants and provide descriptions for each.
export const LabelAndValue: React.FC<Props> = ({
  label,
  value,
  variant = 'basic',
  fontSize = '0.875rem',
  whiteSpace = undefined,
  xs = false,
  sm = false,
  md = false,
  lg = false,
  isIndented = false,
  fontWeight = '500',
  hasBorder = false,
  dangerouslySetHTML = false,
  setHTML = () => null,
  isMonetaryValue = false,
  isNegativeValue = false,
  onClick = () => null,
}) => {
  const valueText = calculateValueText({ value, setHTML, dangerouslySetHTML });

  const isUndefined = typeof value === 'undefined';
  const isBasic = variant === 'basic';
  const isButton = variant === 'button';
  const isAccounting = variant === 'accounting';
  const isDollar = variant === 'dollar';

  return (
    <>
      {isBasic && (
        <Grid container>
          <Grid item xs={xs} sm={sm} md={md} lg={lg}>
            <LabelText style={{ fontSize: fontSize, fontWeight }}>
              {isIndented ? <>&nbsp;&nbsp;</> : null}
              {label}:
            </LabelText>
          </Grid>
          <Grid item>
            <ValueText variant="body2" style={{ fontSize: fontSize, whiteSpace }}>
              {isMonetaryValue ? `$${valueText}` : valueText}
            </ValueText>
          </Grid>
        </Grid>
      )}
      {/* Useful for when you want to display financial data that will note a positive or negative value with brackets. ie ($200) would represent -$200*/}
      {isAccounting && (
        <Grid
          container
          style={
            hasBorder
              ? {
                borderBottom: '1px solid #e0e0e0',
                justifyContent: 'space-between',
                marginBottom: '8px',
                paddingBottom: '8px',
              }
              : { justifyContent: 'space-between' }
          }
        >
          <Grid item xs={xs} sm={sm} md={md}>
            <LabelText style={{ fontSize: fontSize, fontWeight: fontWeight }}>
              {isIndented ? <>&nbsp;&nbsp;</> : null} {label}:
            </LabelText>
          </Grid>
          <Grid item>
            {isUndefined ? (
              <Skeleton width="100px">
                <ValueText variant="body2">.</ValueText>
              </Skeleton>
            ) : (
              <ValueText variant="body2" style={{ fontSize: fontSize }}>
                {isNegativeValue ? '-' : null}
                {isMonetaryValue ? `$${valueText}` : valueText}
              </ValueText>
            )}
          </Grid>
        </Grid>
      )}
      {/* Display a simple dollar sign */}
      {isDollar ? (
        <Grid container>
          <Grid item xs={xs} sm={sm} md={md} lg={lg}>
            <ValueText variant="body2">{label}:</ValueText>
          </Grid>
          <Grid item>
            <LabelText>{isMonetaryValue ? `$${valueText}` : valueText}</LabelText>
          </Grid>
        </Grid>
      ) : null}
      {isButton && (
        <Grid container>
          <Grid item xs={xs} sm={sm} md={md} lg={lg}>
            <LabelText style={{ fontSize: fontSize, fontWeight }}>
              {label}
            </LabelText>
          </Grid>
          <Grid item>
            <Box onClick={onClick}>
              <ButtonBase>
                <Typography
                  style={{ fontWeight: 500, color: theme.palette.secondary.main, borderBottom: '1px solid' }}
                  variant="body2"
                >
                  {value}
                </Typography>
              </ButtonBase>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

function calculateValueText({
  dangerouslySetHTML,
  value,
  setHTML = () => null,
}: Pick<Props, 'dangerouslySetHTML' | 'value' | 'setHTML'>) {
  if (dangerouslySetHTML) {
    return <div dangerouslySetInnerHTML={setHTML()}></div>;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.length === 0) {
    return '--';
  }

  return value;
}

export const placeholderText = '--';
